import { AUTH_URL, PAGINATION, SORT_DIRECTION } from "../constants";
import { xhrUpload } from "utils";
import apiService from "services/api";
import { EVENTS_KEY } from "services/events";

export default {
  authenticate: async () => apiService.post(`${AUTH_URL}/api/v3/auth`),
  login: async (payload) => {
    const { email, password } = payload;
    return apiService.post(`${AUTH_URL}/api/v3/login`, {
      body: JSON.stringify({
        email,
        password,
        distinct_id: localStorage.getItem(EVENTS_KEY),
        google_id: window.gaGlobal?.vid || null,
      }),
    });
  },
  logout: async (refreshToken) =>
    apiService.post(`${AUTH_URL}/api/v3/logout`, {
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
  oauth2GetAuthUri: async (payload) => {
    const { provider } = payload;
    const searchParams = new URLSearchParams("");
    searchParams.append("distinct_id", localStorage.getItem(EVENTS_KEY));
    searchParams.append("google_id", window.gaGlobal?.vid || null);
    return apiService.get(
      `${AUTH_URL}/api/v3/oauth2/${provider}?${searchParams}`
    );
  },
  oauth2Login: async (payload) => {
    const { provider, code, state } = payload;
    const searchParams = new URLSearchParams("");
    searchParams.append("code", code);
    searchParams.append("state", state);
    searchParams.append("distinct_id", localStorage.getItem(EVENTS_KEY));
    searchParams.append("google_id", window.gaGlobal?.vid || null);
    return apiService.get(
      `${AUTH_URL}/api/v3/oauth2/${provider}/code?${searchParams}`
    );
  },
  tou: async () => apiService.post(`${AUTH_URL}/api/v3/tou`),
  checkoutSession: async (payload) => {
    const { price } = payload;
    return apiService.post(`${AUTH_URL}/api/v3/checkout-session`, {
      body: JSON.stringify({ price }),
    });
  },
  customerPortal: async () =>
    apiService.post(`${AUTH_URL}/api/v3/customer-portal`, {
      redirect: "manual",
    }),
  startTrial: async (payload) => {
    const { promo_code } = payload;
    return apiService.post(`${AUTH_URL}/api/v3/start-trial`, {
      body: JSON.stringify({
        promo_code,
      }),
    });
  },
  stopTrial: async () => apiService.post(`${AUTH_URL}/api/v3/stop-trial`),
  signup: async (payload) => {
    const {
      first_name,
      last_name,
      email,
      password,
      promo_code,
      recaptchaToken,
      stripePaymentId,
    } = payload;
    return apiService.post(`${AUTH_URL}/api/v3/signup`, {
      body: JSON.stringify({
        first_name,
        last_name,
        email,
        password,
        promo_code,
        captcha_token: recaptchaToken,
        stripe_payment_id: stripePaymentId,
        distinct_id: localStorage.getItem(EVENTS_KEY),
        google_id: window.gaGlobal?.vid || null,
      }),
    });
  },
  verifyEmail: async (payload) => {
    const { userId, token } = payload;
    return apiService.get(
      `${AUTH_URL}/api/v3/user/verify-email?user_id=${userId}&verification_id=${token}`
    );
  },
  resendVerifyEmail: async () => {
    return apiService.post(`${AUTH_URL}/api/v3/user/resend-verify-email/`);
  },
  calibration: {
    list: async (payload) => {
      const {
        offset = 0,
        limit = PAGINATION.calibrations,
        search = undefined,
      } = payload;
      let url = `${AUTH_URL}/api/v3/calibration?offset=${offset}&limit=${limit}`;
      if (search) {
        url = `${url}&search=${search}`;
      }
      return apiService.get(url);
    },
    get: async (payload) => {
      const { id } = payload;
      return apiService.get(`${AUTH_URL}/api/v3/calibration/${id}`);
    },
    upload: async (payload) => {
      const { data } = payload;
      let fd = new FormData();
      fd.append("data", data);
      return apiService.post(`${AUTH_URL}/api/v3/calibration`, {
        headers: {
          "content-type": undefined,
        },
        body: fd,
      });
    },
    // TODO remove or use old?
    uploadOld: async function (
      file,
      successHandler,
      errorHandler,
      uploadProgressHandler
    ) {
      xhrUpload({
        url: `${AUTH_URL}/api/v3/calibration`,
        successHandler,
        errorHandler,
        uploadProgressHandler,
        file,
      });
    },
    enable: async (payload) => {
      const { calibrationId } = payload;
      return apiService.put(
        `${AUTH_URL}/api/v3/calibration/${calibrationId}/enable`
      );
    },
    enableAuto: async () =>
      apiService.post(`${AUTH_URL}/api/v3/calibration/auto`),
    delete: async (payload) => {
      const { calibrationId, permanent } = payload;
      return apiService.delete(
        `${AUTH_URL}/api/v3/calibration/${calibrationId}?hard_delete=${permanent}`
      );
    },
    rename: async (payload) => {
      const { calibrationId, name } = payload;
      return apiService.patch(
        `${AUTH_URL}/api/v3/calibration/${calibrationId}`,
        {
          body: JSON.stringify({ name }),
        }
      );
    },
  },
  forgotPassword: async (payload) => {
    const { email } = payload;
    return apiService.post(`${AUTH_URL}/api/v3/forgot-password`, {
      body: JSON.stringify({
        email,
        // captcha_token: recaptchaToken,
      }),
    });
  },
  changePassword: async (payload) => {
    const { changePasswordId, password } = payload;
    return apiService.post(`${AUTH_URL}/api/v3/change-password`, {
      body: JSON.stringify({
        change_pass_id: changePasswordId,
        password,
      }),
    });
  },
  getCredits: async () => apiService.get(`${AUTH_URL}/api/v3/credits`),
  promoCode: async (payload) => {
    const { promo_code, place } = payload;
    return apiService.post(`${AUTH_URL}/api/v3/promo-code`, {
      body: JSON.stringify({ promo_code, place }),
    });
  },
  getUsers: async (payload) => {
    const {
      limit = PAGINATION.users,
      offset = 0,
      sort = "email",
      direction = SORT_DIRECTION.desc,
      search: _search,
    } = payload;
    const search = _search ? encodeURIComponent(_search) : undefined;
    let url = `${AUTH_URL}/api/v3/users?limit=${limit}&offset=${offset}&sort=${sort}&direction=${direction}`;
    if (search) {
      url = `${url}&search=${search}`;
    }
    return apiService.get(url);
  },
  getGroups: async (payload) => {
    const { limit = PAGINATION.users, offset = 0, search: _search } = payload;
    const search = _search ? encodeURIComponent(_search) : undefined;
    let url = `${AUTH_URL}/api/v3/groups?limit=${limit}&offset=${offset}`;
    if (search) {
      url = `${url}&search=${search}`;
    }
    return apiService.get(url);
  },
  getOwners: async (payload) => {
    const { ids } = payload;
    return apiService.post(`${AUTH_URL}/api/v3/owners`, {
      body: JSON.stringify({ ids }),
    });
  },
  trust: async (payload) => {
    const { userId, level } = payload;
    return apiService.patch(`${AUTH_URL}/api/v3/${userId}/trust`, {
      body: JSON.stringify({
        t_level: level,
      }),
    });
  },
  updateUserDomainInfo: async (payload) => {
    const { profession, industry } = payload;
    return apiService.put(`${AUTH_URL}/api/v3/domain-info`, {
      body: JSON.stringify({ profession, industry }),
    });
  },
  getSubscriptions: async () =>
    apiService.get(`${AUTH_URL}/api/v3/subscriptions`),
  events: async (body) =>
    apiService.post(`${AUTH_URL}/api/v3/events`, {
      headers: {
        "track-token": process.env.REACT_APP_EVENTS_TRACK_TOKEN,
      },
      body: JSON.stringify(body),
    }),
  sendGoogleEvent: async (payload) =>
    apiService.post(`${AUTH_URL}/api/v3/events/google`, {
      headers: {
        "track-token": process.env.REACT_APP_EVENTS_TRACK_TOKEN,
      },
      body: JSON.stringify(payload),
    }),
  getUser: async (payload) => {
    const { userId } = payload;
    return apiService.get(`${AUTH_URL}/api/v3/users/${userId}`);
  },
  sendFeedback: async (payload) => {
    const { type, topic, name, email, description, files } = payload;
    let fd = new FormData();
    fd.append("type", type);
    fd.append("topic", topic);
    fd.append("name", name);
    fd.append("email", email);
    fd.append("description", description);
    files.forEach((f) => fd.append("files", f));

    return apiService.post(`${AUTH_URL}/api/v3/feedback`, {
      headers: {
        "content-type": undefined,
      },
      body: fd,
    });
  },
  updateUserLocalStorage: async (payload) => {
    const data = payload;
    return apiService.put(`${AUTH_URL}/api/v3/local-storage`, {
      body: JSON.stringify({ local_storage: data }),
    });
  },
  updateDemoWizard: async (payload) => {
    const data = payload;
    return apiService.put(`${AUTH_URL}/api/v3/demo-wizard`, {
      body: JSON.stringify(data),
    });
  },
  deleteAccount: async () => {
    return apiService.delete(`${AUTH_URL}/api/v3/profile`);
  },
  updateSettings: async (payload) => {
    const data = payload;
    return apiService.put(`${AUTH_URL}/api/v3/settings`, {
      body: JSON.stringify(data),
    });
  },
  apiKey: {
    get: async () => {
      return apiService.get(`${AUTH_URL}/api/v3/api-key`);
    },
    create: async (payload) => {
      const { expiresAt } = payload;
      return apiService.post(`${AUTH_URL}/api/v3/api-key`, {
        body: JSON.stringify({ expires_at: expiresAt }),
      });
    },
    delete: async () => {
      return apiService.delete(`${AUTH_URL}/api/v3/api-key`);
    },
  },
  npsSurvey: async (payload) => {
    const data = payload;
    return apiService.post(`${AUTH_URL}/api/v3/surveys/nps`, {
      body: JSON.stringify(data),
    });
  },
};
